import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

/**
 * VerifyPage (doc-access-003) — token-gated, NOT public. The page is reached
 * only via an opaque, revocable share link (/verify/:token) a member or admin
 * deliberately handed to a counterparty. It resolves the token to a single
 * document's NON-PII attestation (verification ID, hash, signing dates, terms
 * version) and lets the holder upload their copy to confirm it matches. There
 * is no lookup by verification ID and no enumeration.
 */
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const Attestation = ({ data }) => (
  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
      <div>
        <dt className="text-xs uppercase tracking-wide text-slate-500">Verification ID</dt>
        <dd className="font-medium text-slate-900">{data.verification_id || "—"}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide text-slate-500">Terms version</dt>
        <dd className="font-medium text-slate-900">{data.terms_version || "—"}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide text-slate-500">Executed (created)</dt>
        <dd className="font-medium text-slate-900">{fmtDate(data.created_at)}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide text-slate-500">Investor signed</dt>
        <dd className="font-medium text-slate-900">{fmtDate(data.investor_signed_at)}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide text-slate-500">Partner countersigned</dt>
        <dd className="font-medium text-slate-900">{fmtDate(data.partner_signed_at)}</dd>
      </div>
      <div className="sm:col-span-2">
        <dt className="text-xs uppercase tracking-wide text-slate-500">Document SHA-256</dt>
        <dd className="break-all font-mono text-xs text-slate-700">
          {data.signed_pdf_sha256 || "—"}
        </dd>
      </div>
    </dl>
    <p className="mt-3 text-xs text-slate-500">
      Re-hash your copy of the document (SHA-256) and compare it to the digest
      above, or upload it below to confirm the bytes are unaltered. This record
      intentionally excludes participant identity and amount.
    </p>
  </div>
);

const ResultBanner = ({ ok, okText, badText }) => (
  <div
    className={`flex items-center gap-2 rounded-lg p-3 ${
      ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"
    }`}
  >
    {ok ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span className="font-medium">{ok ? okText : badText}</span>
  </div>
);

const VerifyPage = () => {
  const { token } = useParams();
  const [record, setRecord] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [file, setFile] = useState(null);
  const [upResult, setUpResult] = useState(null);
  const [upError, setUpError] = useState(null);
  const [upLoading, setUpLoading] = useState(false);

  useEffect(() => {
    let active = true;
    // Reset per-token state so navigating between links doesn't show stale data.
    setLoading(true);
    setRecord(null);
    setLoadError(null);
    setUpResult(null);
    setUpError(null);
    setUpLoading(false);
    setFile(null);
    (async () => {
      try {
        const r = await api.get(`/verify/${encodeURIComponent(token)}`);
        if (active) setRecord(r.data);
      } catch (err) {
        if (active)
          setLoadError(
            err.response?.data?.detail ||
              "This verification link is invalid, expired, or has been revoked.",
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const verifyUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUpLoading(true);
    setUpResult(null);
    setUpError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/verify/${encodeURIComponent(token)}`, form);
      setUpResult(r.data);
    } catch (err) {
      setUpError(err.response?.data?.detail || "Verification failed. Try again.");
    } finally {
      setUpLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="mb-6 flex items-center gap-3">
        <ShieldCheck className="h-8 w-8 text-emerald-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Document verification</h1>
          <p className="text-sm text-slate-600">
            Confirm a shared participation agreement against EchoSense Capital Club&apos;
            signing record.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Shared verification link</CardTitle>
          <CardDescription>
            This link confirms the authenticity of a single shared document.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-slate-500">Resolving link…</p>}

          {loadError && <ResultBanner ok={false} badText={loadError} />}

          {record?.found && (
            <>
              <ResultBanner ok okText="Verified — this link matches a recorded signing event." />
              <Attestation data={record} />

              <form onSubmit={verifyUpload} className="mt-6 space-y-3">
                <Label htmlFor="pdf">Optional: upload your copy to check it matches</Label>
                <Input
                  id="pdf"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <Button type="submit" disabled={upLoading || !file}>
                  {upLoading ? "Checking…" : "Check this PDF"}
                </Button>
              </form>

              {upError && (
                <div className="mt-4">
                  <ResultBanner ok={false} badText={upError} />
                </div>
              )}
              {upResult && (
                <div className="mt-4">
                  {upResult.match ? (
                    <ResultBanner ok okText="Match — these bytes are the recorded signed document." />
                  ) : (
                    <>
                      <ResultBanner
                        ok={false}
                        badText="No match — this file differs from the shared signed document (or has been altered)."
                      />
                      {upResult.sha256 && (
                        <p className="mt-2 break-all font-mono text-xs text-slate-500">
                          Computed SHA-256: {upResult.sha256}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          <div className="mt-6 flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
            <Badge variant="outline" className="shrink-0">Privacy</Badge>
            <span>
              This link reports only the verification ID, document hash, signing
              dates, and terms version — never participant names, contact details,
              or amounts. It can be revoked by the document owner at any time.
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyPage;
